/**
 * Queues Report Page
 * 
 * Order status distribution analysis over time.
 * Shows horizontal stacked bar chart with drill-down capabilities.
 * 
 * Features:
 * - Time view selector (Daily/Weekly/Monthly)
 * - Date range selector
 * - Summary metrics
 * - Horizontal stacked bar chart
 * - Status summary table with drill-down
 * - CSV export
 */

import { format } from 'date-fns'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, Input, Label } from '../components/ui'
import { queuesAPI } from '../services/api'
import OrderStatusInfo from '../components/OrderStatusInfo'
import { orderStatusInfo } from '../config/orderStatuses'
import { useAuthStore } from '../stores/authStore'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts'
import {
  Package,
  RefreshCw,
  AlertCircle,
  Loader2,
  CheckCircle,
  Clock,
  Truck,
  XCircle,
  ChevronDown,
  ChevronUp,
  Download,
  Calendar,
  BarChart3
} from 'lucide-react'

// Status color mapping
const STATUS_COLORS = {
  'COMPLETED': '#10B981',
  'COMPLETED_LOCAL': '#32D74B',
  'IN_SHIPPING': '#3B82F6',
  'AT_PHARMACY': '#FB923C',
  'ROUTING_COMPLETED': '#EC4899',
  'ROUTING_FAILED': '#EF4444',
  'CANCELLED': '#64748B',
  'CANCELLED_REORDER': '#1F2937',
  'SCRIPT_WRITTEN': '#F59E0B',
  'WAITING_REFILL': '#94A3B8',
  'WAITING_APPOINTMENT': '#0EA5E9',
  'PAYMENT_SUCCEEDED': '#8B5CF6',
  'NURSE_QUALIFIED': '#14B8A6',
  'ROUTED_TO_DOCTOR': '#F59E0B',
  'ROUTED_TO_PHARMACY': '#EC4899',
  'FILL_CREATED': '#06B6D4',
  'LABEL_CREATED': '#6B7280',
  'UNKNOWN': '#D1D5DB'
}

// Status order for consistent chart stacking
const STATUS_ORDER = [
  'COMPLETED',
  'COMPLETED_LOCAL',
  'IN_SHIPPING',
  'AT_PHARMACY',
  'ROUTING_COMPLETED',
  'ROUTING_FAILED',
  'CANCELLED',
  'CANCELLED_REORDER',
  'SCRIPT_WRITTEN',
  'FILL_CREATED',
  'LABEL_CREATED',
  'WAITING_APPOINTMENT',
  'ROUTED_TO_PHARMACY',
  'ROUTED_TO_DOCTOR',
  'NURSE_QUALIFIED',
  'PAYMENT_SUCCEEDED',
  'WAITING_REFILL',
  'UNKNOWN'
]

// View type options
const VIEW_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
]

// Date range options based on view type
const DATE_RANGE_OPTIONS = {
  daily: [
    { value: 7, label: 'Last 7 days' },
    { value: 14, label: 'Last 14 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 60, label: 'Last 60 days' }
  ],
  weekly: [
    { value: 28, label: 'Last 4 weeks' },
    { value: 56, label: 'Last 8 weeks' },
    { value: 84, label: 'Last 12 weeks' }
  ],
  monthly: [
    { value: 90, label: 'Last 3 months' },
    { value: 180, label: 'Last 6 months' },
    { value: 365, label: 'Last 12 months' }
  ]
}

// Status icon mapping
const getStatusIcon = (status) => {
  switch (status) {
    case 'COMPLETED':
    case 'COMPLETED_LOCAL':
      return CheckCircle
    case 'IN_SHIPPING':
      return Truck
    case 'AT_PHARMACY':
    case 'ROUTING_COMPLETED':
    case 'SCRIPT_WRITTEN':
      return Clock
    case 'CANCELLED':
    case 'CANCELLED_REORDER':
    case 'ROUTING_FAILED':
      return XCircle
    default:
      return Package
  }
}

/**
 * Format number with commas
 */
function formatNumber(num) {
  return new Intl.NumberFormat('en-US').format(num)
}

/**
 * Format date for display
 */
function formatDate(dateStr) {
  if (!dateStr) return 'N/A'
  // Handle ISO dates
  if (dateStr.includes('T')) {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  return dateStr
}

/**
 * Summary Card Component
 */
function SummaryCard({ title, value, subtitle, icon: Icon, color, loading }) {
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-1/3"></div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">{title}</span>
              {Icon && (
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
                  <Icon className="h-4 w-4" style={{ color }} />
                </div>
              )}
            </div>
            <div className="text-2xl font-bold tracking-tight">{value}</div>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Custom Tooltip for Horizontal Bar Chart
 */
function CustomBarTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null
  
  // Calculate total for percentage
  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0)
  
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[220px] max-h-[300px] overflow-y-auto">
      <p className="font-medium text-sm mb-2 border-b border-border pb-2">{label}</p>
      <p className="text-xs text-muted-foreground mb-2">Total: {formatNumber(total)} orders</p>
      <div className="space-y-1">
        {payload
          .filter(entry => entry.value > 0)
          .sort((a, b) => b.value - a.value)
          .map((entry, index) => {
            const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : 0
            return (
              <div key={index} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5">
                  <span 
                    className="w-2 h-2 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="truncate max-w-[120px]">{entry.name}</span>
                </span>
                <span className="font-medium ml-2">
                  {formatNumber(entry.value)} ({percentage}%)
                </span>
              </div>
            )
          })}
      </div>
    </div>
  )
}

/**
 * Status Summary Row Component
 */
function StatusSummaryRow({ status, count, total, onViewOrders, isExpanded, orders, loadingOrders }) {
  const Icon = getStatusIcon(status)
  const color = STATUS_COLORS[status] || STATUS_COLORS.UNKNOWN
  const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0
  
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div 
        className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer transition-colors"
        onClick={() => onViewOrders(status)}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-3 h-3 rounded-full flex-shrink-0" 
            style={{ backgroundColor: color }}
          />
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{status}</span>
          <OrderStatusInfo status={status} />
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">{percentage}%</span>
          <span className="font-semibold min-w-[60px] text-right">{formatNumber(count)}</span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>
      
      {isExpanded && (
        <div className="border-t border-border bg-muted/30 p-4">
          {loadingOrders ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading orders...</span>
            </div>
          ) : orders && orders.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Showing {orders.length} of {count} orders
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    exportToCSV(orders, status)
                  }}
                >
                  <Download className="h-3 w-3 mr-1" />
                  Export CSV
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 font-medium">Order ID</th>
                      <th className="text-left py-2 px-3 font-medium">Entity ID</th>
                      <th className="text-left py-2 px-3 font-medium">Created</th>
                      <th className="text-left py-2 px-3 font-medium">Status Date</th>
                      <th className="text-left py-2 px-3 font-medium">Pharmacy</th>
                    </tr>
                  </thead>
                  <tbody>
                      {orders.map((order, idx) => (
                      <tr key={order.id || idx} className="border-b border-border/50 hover:bg-muted/50">
                        <td className="py-2 px-3 font-mono text-xs">
                          {order.id && order.patientId ? (
                            <a
                              href={`https://core-odin.5axis.health/dashboard/customers/${order.patientId}/orders/${order.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {order.id}
                            </a>
                          ) : (
                            <span className="select-all">{order.id || 'N/A'}</span>
                          )}
                        </td>
                        <td className="py-2 px-3 whitespace-nowrap" title={order.entityId || undefined}>
                          {order.entityId || 'N/A'}
                        </td>
                        <td className="py-2 px-3 text-xs">{formatDate(order.creationDate)}</td>
                        <td className="py-2 px-3 text-xs">{formatDate(order.statusDate)}</td>
                        <td className="py-2 px-3 whitespace-nowrap" title={order.pharmacyName || undefined}>
                          {order.pharmacyName || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">No orders found</p>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Export orders to CSV
 */
function exportToCSV(orders, status) {
  const headers = ['Order ID', 'Patient ID', 'Entity ID', 'Status', 'Created', 'Status Date', 'Pharmacy']
  const rows = orders.map(o => [
    o.id || '',
    o.patientId || '',
    o.entityId || '',
    o.status || status,
    o.creationDate || '',
    o.statusDate || '',
    o.pharmacyName || ''
  ])
  
  const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `orders_${status.toLowerCase()}_${format(new Date(), 'yyyy-MM-dd')}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export default function QueuesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  
  // View controls
  const [viewType, setViewType] = useState(searchParams.get('view') || 'daily')
  const [daysBack, setDaysBack] = useState(parseInt(searchParams.get('days')) || 7)
  
  // Drill-down state
  const [expandedStatus, setExpandedStatus] = useState(null)
  const [statusOrders, setStatusOrders] = useState({})
  const [loadingOrders, setLoadingOrders] = useState(false)
  
  const clinicId = searchParams.get('clinic') || 'tmates'
  const { currentClinicId } = useAuthStore()
  const effectiveClinicId = currentClinicId || clinicId
  
  // Fetch status distribution data
  const fetchData = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) {
      setIsRefreshing(true)
    } else {
      setLoading(true)
    }
    setError(null)
    
    try {
      console.log('[QUEUES] Fetching data for clinic:', effectiveClinicId, 'view:', viewType, 'days:', daysBack)
      
      const response = await queuesAPI.getStatusDistribution({
        viewType,
        daysBack
      })
      
      if (response.data?.success) {
        setData(response.data.data)
        console.log('[QUEUES] Data loaded:', response.data.data.summary)
      } else {
        throw new Error(response.data?.error || 'Failed to load data')
      }
    } catch (err) {
      console.error('[QUEUES] Error fetching data:', err)
      setError(err.response?.data?.message || err.message || 'Failed to load queue data')
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [effectiveClinicId, viewType, daysBack])
  
  // Fetch orders for a specific status
  const fetchStatusOrders = useCallback(async (status) => {
    if (statusOrders[status]) {
      // Already loaded
      return
    }
    
    setLoadingOrders(true)
    try {
      const response = await queuesAPI.getOrdersByStatus(status, {
        daysBack,
        limit: 100
      })
      
      if (response.data?.success) {
        setStatusOrders(prev => ({
          ...prev,
          [status]: response.data.data.orders
        }))
      }
    } catch (err) {
      console.error('[QUEUES] Error fetching orders for status:', status, err)
    } finally {
      setLoadingOrders(false)
    }
  }, [daysBack, statusOrders])
  
  // Handle status row click
  const handleViewOrders = useCallback((status) => {
    if (expandedStatus === status) {
      setExpandedStatus(null)
    } else {
      setExpandedStatus(status)
      fetchStatusOrders(status)
    }
  }, [expandedStatus, fetchStatusOrders])
  
  // Update URL params when controls change
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      params.set('view', viewType)
      params.set('days', daysBack.toString())
      if (effectiveClinicId) {
        params.set('clinic', effectiveClinicId)
      }
      return params
    })
  }, [viewType, daysBack, effectiveClinicId, setSearchParams])
  
  // Fetch data on mount and when params change
  useEffect(() => {
    fetchData()
  }, [fetchData])
  
  // Reset orders cache when view changes
  useEffect(() => {
    setStatusOrders({})
    setExpandedStatus(null)
  }, [viewType, daysBack])
  
  // Transform data for the horizontal stacked bar chart
  const chartData = useMemo(() => {
    if (!data?.distribution) return []
    
    // Group by date
    const dateMap = {}
    data.distribution.forEach(item => {
      if (!dateMap[item.date]) {
        dateMap[item.date] = { date: item.date }
      }
      dateMap[item.date][item.status] = item.count
    })
    
    // Convert to array and sort by date descending (newest first for horizontal chart)
    return Object.values(dateMap).sort((a, b) => b.date.localeCompare(a.date))
  }, [data])
  
  // Get unique statuses present in data
  const activeStatuses = useMemo(() => {
    if (!data?.summary?.statusCounts) return []
    
    return STATUS_ORDER.filter(status => data.summary.statusCounts[status] > 0)
  }, [data])
  
  // Sort status counts for summary table
  const sortedStatusCounts = useMemo(() => {
    if (!data?.summary?.statusCounts) return []
    
    return Object.entries(data.summary.statusCounts)
      .sort(([, a], [, b]) => b - a)
      .map(([status, count]) => ({ status, count }))
  }, [data])
  
  // Handle view type change
  const handleViewTypeChange = (newView) => {
    setViewType(newView)
    // Reset days back to first option for new view type
    setDaysBack(DATE_RANGE_OPTIONS[newView][0].value)
  }
  
  // Error state
  if (error && !loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Queues Report</h1>
        </div>
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to Load Report</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => fetchData()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </Card>
      </div>
    )
  }
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Queues Report</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Order status distribution analysis • Monitor order flow and identify bottlenecks
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchData(true)}
          disabled={isRefreshing}
        >
          {isRefreshing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>
      
      {/* Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* View Type Selector */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground flex items-center gap-1">
                <BarChart3 className="h-4 w-4" />
                View:
              </Label>
              <div className="flex gap-1">
                {VIEW_OPTIONS.map(option => (
                  <Button
                    key={option.value}
                    variant={viewType === option.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleViewTypeChange(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Date Range Selector */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Range:
              </Label>
              <div className="flex gap-1">
                {DATE_RANGE_OPTIONS[viewType].map(option => (
                  <Button
                    key={option.value}
                    variant={daysBack === option.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDaysBack(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Orders"
          value={loading ? '...' : formatNumber(data?.summary?.totalOrders || 0)}
          subtitle={`In selected ${viewType} view`}
          icon={Package}
          color="#3B82F6"
          loading={loading}
        />
        <SummaryCard
          title="Status Types"
          value={loading ? '...' : data?.summary?.statusTypes || 0}
          subtitle="Unique statuses"
          icon={BarChart3}
          color="#8B5CF6"
          loading={loading}
        />
        <SummaryCard
          title="Completion Rate"
          value={loading ? '...' : `${data?.summary?.completionRate || 0}%`}
          subtitle={
            data?.summary?.waitingRefillOrders
              // Named rather than implied: the denominator is smaller than
              // Total Orders, and a reader who checks the arithmetic should
              // find the reason on the card.
              ? `of ${formatNumber(data.summary.completableOrders || 0)}, excl. ${formatNumber(data.summary.waitingRefillOrders)} waiting refill`
              : 'COMPLETED + COMPLETED_LOCAL'
          }
          icon={CheckCircle}
          color="#10B981"
          loading={loading}
        />
        <SummaryCard
          title="Latest Order"
          value={loading ? '...' : data?.summary?.latestOrderDate || 'N/A'}
          subtitle="Most recent order date"
          icon={Clock}
          color="#F59E0B"
          loading={loading}
        />
      </div>
      
      {/* Horizontal Stacked Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Order Status Distribution by {viewType === 'daily' ? 'Day' : viewType === 'weekly' ? 'Week' : 'Month'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-[500px] flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-[500px] flex items-center justify-center text-muted-foreground">
              No data available for the selected period
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(400, chartData.length * 35 + 100)}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 20, right: 30, left: 80, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                <XAxis type="number" tickFormatter={formatNumber} />
                <YAxis 
                  type="category" 
                  dataKey="date" 
                  width={70}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip content={<CustomBarTooltip />} />
                <Legend 
                  wrapperStyle={{ paddingTop: 20 }}
                  formatter={(value) => <span className="text-xs" title={orderStatusInfo(value) || undefined}>{value}</span>}
                />
                {activeStatuses.map(status => (
                  <Bar
                    key={status}
                    dataKey={status}
                    stackId="stack"
                    fill={STATUS_COLORS[status] || STATUS_COLORS.UNKNOWN}
                    name={status}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
      
      {/* Status Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Status Summary
            <span className="text-sm font-normal text-muted-foreground ml-2">
              Click to view orders
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse h-12 bg-muted rounded-lg" />
              ))}
            </div>
          ) : sortedStatusCounts.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No status data available</p>
          ) : (
            <div className="space-y-2">
              {sortedStatusCounts.map(({ status, count }) => (
                <StatusSummaryRow
                  key={status}
                  status={status}
                  count={count}
                  total={data?.summary?.totalOrders || 0}
                  onViewOrders={handleViewOrders}
                  isExpanded={expandedStatus === status}
                  orders={statusOrders[status]}
                  loadingOrders={loadingOrders && expandedStatus === status}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

